import {
  Alert,
  Anchor,
  Button,
  Card,
  Grid,
  Group,
  Image,
  List,
  Radio,
  SimpleGrid,
  Spoiler,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  RISQ_STATUSES,
  RISQ_STATUS_ICONS,
  resolveChecklist,
  type RisqStatus,
} from '@opti-sea/risq';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useWorkplace } from '../../../core/access';
import { PageTitle, QueryState, StatusBadge, usePositionNames } from '../../../ui/components';
import { formatDateTime } from '../../../ui/format';
import { useItemDetail, useNames, useRpc, useSignedUrls, useVesselItems } from '../data';
import type { VesselItem } from '../model';
import { FlagBadges } from './flagBadges';
import { useEventText } from './ActivityPage';

/** Icons beside the words "Checked" / "Not checked" (the words carry the meaning). */
const CHECKED_ICON = '✓';
const OPEN_ICON = '○';

function StatusCard({ item }: { item: VesselItem }) {
  const { t } = useTranslation(['dashboard', 'common']);
  const [choice, setChoice] = useState<RisqStatus>(item.status);
  const [note, setNote] = useState('');
  const refresh = [
    ['items', item.vesselId],
    ['item', item.id],
    ['events', item.vesselId],
  ] as const;
  const setStatus = useRpc('risq_set_status', refresh);
  const addNote = useRpc('risq_add_note', refresh);
  const error = setStatus.error ?? addNote.error;
  return (
    <Card withBorder>
      <Stack>
        <Title order={2} size="h3">
          {t('question.statusTitle')}
        </Title>
        <Radio.Group value={choice} onChange={(value) => setChoice(value as RisqStatus)}>
          <Stack gap="xs">
            {RISQ_STATUSES.map((s) => (
              <Radio
                key={s}
                value={s}
                label={t('status.option', {
                  icon: RISQ_STATUS_ICONS[s],
                  word: t(`common:status.${s}`),
                })}
                description={t(`common:statusMeaning.${s}`)}
              />
            ))}
          </Stack>
        </Radio.Group>
        <Group>
          <Button
            disabled={choice === item.status}
            loading={setStatus.isPending}
            onClick={() =>
              setStatus.mutate({
                p_item_id: item.id,
                p_to_status: choice,
                p_from_status: item.status,
              })
            }
          >
            {t('question.saveStatus')}
          </Button>
          {setStatus.isSuccess ? <Text c="green.9">{t('common.saved')}</Text> : null}
        </Group>
        <Textarea
          label={t('question.noteLabel')}
          description={t('question.noteHint')}
          autosize
          minRows={2}
          maxLength={4000}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
        <Group>
          <Button
            variant="default"
            disabled={note.trim() === ''}
            loading={addNote.isPending}
            onClick={() =>
              addNote.mutate({ p_item_id: item.id, p_text: note }, { onSuccess: () => setNote('') })
            }
          >
            {t('question.addNote')}
          </Button>
          {addNote.isSuccess ? <Text c="green.9">{t('common.saved')}</Text> : null}
        </Group>
        {error === null ? null : (
          <Alert color="red" role="alert">
            {t('common.saveFailed', { message: error.message })}
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

/** D5 Question on a vessel: text, checklist, company notes, status, notes, photos, history. */
export function QuestionPage() {
  const { t } = useTranslation(['dashboard', 'common']);
  const { vesselId = '', itemId = '' } = useParams();
  const { vessels } = useWorkplace();
  const vessel = vessels.find((v) => v.id === vesselId);
  const { items, loading, error } = useVesselItems(vesselId);
  const item = items?.find((i) => i.id === itemId) ?? null;
  const detail = useItemDetail(itemId, item?.question.id ?? null, item?.question.key ?? null);
  const names = useNames(vesselId);
  const { list, name } = usePositionNames();
  const describe = useEventText();
  const thumbs = useSignedUrls((detail.data?.evidence ?? []).map((e) => e.thumb_path));
  const full = useSignedUrls((detail.data?.evidence ?? []).map((e) => e.storage_path));

  if (vessel === undefined) {
    return <Text>{t('vessel.notFound')}</Text>;
  }
  return (
    <QueryState
      loading={loading || (item !== null && detail.isPending)}
      error={error ?? detail.error}
    >
      {() => {
        if (item === null || detail.data === undefined) {
          return <Text>{t('question.notFound')}</Text>;
        }
        const d = detail.data;
        const checked = new Set(
          d.checks.filter((c) => c.checked).map((c) => c.wtc_item_id ?? c.company_wtc_item_id),
        );
        const points = resolveChecklist(
          d.points.map((p) => ({
            id: p.id,
            itemKey: p.item_key,
            sortOrder: p.sort_order,
            textEn: p.text_en,
          })),
          d.ownPoints.map((p) => ({
            id: p.id,
            itemKey: p.item_key,
            isOwn: p.is_own,
            textEn: p.text_en,
            hidden: p.hidden,
            sortOrder: p.sort_order,
          })),
        );
        const ims = d.notes.filter((n) => n.kind === 'ims_reference');
        const remarks = d.notes.filter((n) => n.kind === 'office_remark');
        const who = (id: string | null) =>
          id === null ? t('activity.system') : (names.data?.get(id) ?? t('activity.someone'));
        return (
          <>
            <PageTitle
              title={t('question.title', { code: item.question.code })}
              back={{
                to: `/vessels/${vessel.id}/questions`,
                label: t('questions.title', { vessel: vessel.name }),
              }}
            >
              <Anchor component={Link} to={`/content/${encodeURIComponent(item.question.key)}`}>
                {t('question.edit')}
              </Anchor>
            </PageTitle>
            <Grid gap="lg">
              <Grid.Col span={{ base: 12, lg: 7 }}>
                <Stack gap="lg">
                  <Card withBorder>
                    <Stack>
                      <Group gap="xs">
                        <StatusBadge status={item.status} />
                        <FlagBadges flags={item.flags} />
                      </Group>
                      {item.attentionFlag ? (
                        <Alert color="red" title={t('question.attention')} />
                      ) : null}
                      {item.needsRecheck ? (
                        <Alert color="orange" title={t('question.recheck')} />
                      ) : null}
                      <Text>{item.question.textEn}</Text>
                      <Text size="sm">
                        {t('question.responsible', {
                          positions: list(item.positions.codes, t('questions.unassigned')),
                          source: t(`layers.source.${item.positions.source}`),
                        })}
                      </Text>
                      {item.question.adviceEn === null ? null : (
                        <Alert color="blue" title={t('question.advice')}>
                          {item.question.adviceEn}
                        </Alert>
                      )}
                      {item.lastNote === null ? null : (
                        <Text size="sm">{t('question.lastNote', { text: item.lastNote })}</Text>
                      )}
                    </Stack>
                  </Card>
                  <Card withBorder>
                    <Stack>
                      <Title order={2} size="h3">
                        {t('question.checklist', {
                          checked: points.filter((p) => checked.has(p.id)).length,
                          total: points.length,
                        })}
                      </Title>
                      {points.length === 0 ? <Text>{t('question.noPoints')}</Text> : null}
                      <List spacing="xs" listStyleType="none">
                        {points.map((p) => (
                          <List.Item
                            key={p.id}
                            icon={
                              <ThemeIcon
                                size={22}
                                variant="light"
                                color={checked.has(p.id) ? 'green' : 'gray'}
                              >
                                <Text size="xs" fw={700} aria-hidden>
                                  {checked.has(p.id) ? CHECKED_ICON : OPEN_ICON}
                                </Text>
                              </ThemeIcon>
                            }
                          >
                            <Text size="sm">
                              {t(
                                checked.has(p.id) ? 'question.pointChecked' : 'question.pointOpen',
                                {
                                  text: p.text,
                                },
                              )}
                              {p.fromCompany ? t('question.companyPoint') : ''}
                            </Text>
                          </List.Item>
                        ))}
                      </List>
                    </Stack>
                  </Card>
                  <Card withBorder>
                    <Stack>
                      <Title order={2} size="h3">
                        {t('question.companyNotes')}
                      </Title>
                      <Text fw={600}>{t('notes.ims')}</Text>
                      {ims.length === 0 ? <Text size="sm">{t('notes.none')}</Text> : null}
                      {ims.map((n) => (
                        <Text key={n.id} size="sm">
                          {n.text}
                        </Text>
                      ))}
                      <Text fw={600}>{t('notes.remarks')}</Text>
                      {remarks.length === 0 ? <Text size="sm">{t('notes.none')}</Text> : null}
                      {remarks.map((n) => (
                        <Text key={n.id} size="sm">
                          {n.text}
                        </Text>
                      ))}
                    </Stack>
                  </Card>
                  <Card withBorder>
                    <Stack>
                      <Title order={2} size="h3">
                        {t('question.guidance')}
                      </Title>
                      <Spoiler
                        maxHeight={120}
                        showLabel={t('common.showMore')}
                        hideLabel={t('common.showLess')}
                      >
                        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                          {item.question.guidanceEn ?? t('question.noGuidance')}
                        </Text>
                      </Spoiler>
                    </Stack>
                  </Card>
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, lg: 5 }}>
                <Stack gap="lg">
                  <StatusCard key={item.status} item={item} />
                  <Card withBorder>
                    <Stack>
                      <Title order={2} size="h3">
                        {t('question.photos', { count: d.evidence.length })}
                      </Title>
                      {d.evidence.length === 0 ? (
                        <Text size="sm">{t('question.noPhotos')}</Text>
                      ) : null}
                      <SimpleGrid cols={2}>
                        {d.evidence.map((e) => {
                          const thumb = thumbs.data?.get(e.thumb_path) ?? null;
                          const link = full.data?.get(e.storage_path) ?? null;
                          const caption = t('question.photoCaption', {
                            who: who(e.taken_by),
                            position: e.taken_position === null ? '' : name(e.taken_position),
                            when: formatDateTime(e.taken_at),
                          });
                          return (
                            <Stack key={e.id} gap={4}>
                              {thumb === null ? (
                                <Text size="sm" c="dimmed">
                                  {t('question.photoMissing')}
                                </Text>
                              ) : (
                                <Image src={thumb} alt={caption} radius="sm" />
                              )}
                              <Text size="xs">{caption}</Text>
                              {link === null ? null : (
                                <Anchor href={link} target="_blank" rel="noreferrer" size="sm">
                                  {t('question.photoOpen')}
                                </Anchor>
                              )}
                            </Stack>
                          );
                        })}
                      </SimpleGrid>
                    </Stack>
                  </Card>
                  <Card withBorder>
                    <Stack gap="xs">
                      <Title order={2} size="h3">
                        {t('question.history')}
                      </Title>
                      {d.events.length === 0 ? <Text size="sm">{t('activity.empty')}</Text> : null}
                      {d.events.map((e) => (
                        <Stack key={e.id} gap={0}>
                          <Text size="sm">{describe(e)}</Text>
                          <Text size="xs" c="dimmed">
                            {t('question.by', {
                              who:
                                e.actor_position === null
                                  ? t('activity.office', { name: who(e.actor_id) })
                                  : t('activity.person', {
                                      name: who(e.actor_id),
                                      position: name(e.actor_position),
                                    }),
                              when: formatDateTime(e.client_created_at),
                            })}
                          </Text>
                        </Stack>
                      ))}
                    </Stack>
                  </Card>
                </Stack>
              </Grid.Col>
            </Grid>
          </>
        );
      }}
    </QueryState>
  );
}
